import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  deleteConversation,
  getConversation,
  streamMessage,
} from "./api";
import OutreachDraftCard from "./OutreachDraftCard";
import OutreachReviewOverlay from "./OutreachReviewOverlay";
import ValuationMethodPanel from "./components/ValuationMethodPanel";

// Speglar METHOD_TITLES i ValuationMethodPanel.jsx — används i
// follow-up-meddelandet så Sigge får det svenska namnet, inte
// ebitda/revenue/asset-slugen.
const METHOD_LABELS = {
  ebitda: "EBITDA-multipel",
  revenue: "Omsättningsmultipel",
  asset: "Substansvärde",
};

// A single conversation thread + input. Handles:
//   - load conversation history on :id change
//   - optimistic user message + streamed assistant reply
//   - error state (network / OpenAI failure)
//   - auto-scroll to bottom during streaming
//   - "initial message" handoff from the empty-state home (via location.state)
export default function ConversationView({
  onConversationChanged,
  // Optional embed overrides. Callers that mount ConversationView
  // outside the /c/:id route (Deal Setup, Deal Workspace right pane)
  // pass an explicit conversationId + can hide the built-in header
  // and the delete button. Behavior for the default /c/:id route is
  // unchanged when these props are omitted.
  conversationId: conversationIdProp,
  hideHeader = false,
  hideDelete = false,
}) {
  const params = useParams();
  const id = conversationIdProp ?? params.id;
  const navigate = useNavigate();
  const location = useLocation();

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const [draft, setDraft] = useState("");
  // Live agent tool activity for the CURRENT turn only. Cleared once
  // the assistant's final message arrives (or on error). Never shows
  // tool arguments or result payloads — just the friendly label + a
  // running/completed/failed status pill.
  const [toolActivity, setToolActivity] = useState([]);
  // Outreach drafts announced by the backend for this conversation.
  // Each entry is the light `{draft_id, acquirer_name, subject,
  // match_score, status}` payload from an `outreach_draft_ready` SSE
  // event — full detail is fetched on demand by the review overlay.
  // Persisted only in memory; if the user reloads the page the drafts
  // are still available via the sidebar list (fetched via /outreach-
  // drafts/).
  const [outreachCards, setOutreachCards] = useState([]);
  const [reviewingDraftId, setReviewingDraftId] = useState(null);
  // Valuation-paneler emit:ade via SSE-eventet `valuation_panel` från
  // compute_valuation-verktyget. Renderas som separata inline-block
  // efter textsammanfattningen. Endast i minnet — vid page-reload
  // måste användaren be Sigge räkna ut värderingen igen. Vi anchor:ar
  // varje panel till det assistant-messagets id som var i tur (om
  // känt) så positionen bevaras när fler meddelanden kommer.
  const [valuationPanels, setValuationPanels] = useState([]);

  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const streamAbortRef = useRef(null);
  // Guard against React StrictMode double-invoking the initial-message
  // effect, which would send the same first message twice.
  const initialSentRef = useRef(false);

  // ── Load conversation on :id change ───────────────────────────────
  useEffect(() => {
    let alive = true;
    const controller = new AbortController();

    setLoading(true);
    setError(null);
    initialSentRef.current = false;

    (async () => {
      try {
        const data = await getConversation(id, { signal: controller.signal });
        if (!alive) return;
        setConversation(data);
        setMessages(data.messages || []);
      } catch (e) {
        if (!alive || e.name === "AbortError") return;
        setError(e?.body?.detail || e.message || "Kunde inte ladda konversationen");
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      controller.abort();
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
    };
  }, [id]);

  // ── Auto-scroll ───────────────────────────────────────────────────
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  // ── Send a message + consume stream ───────────────────────────────
  const send = useCallback(
    async (text) => {
      const content = (text ?? "").trim();
      if (!content || streaming) return;

      setError(null);
      setStreaming(true);
      setToolActivity([]);

      // Optimistic user + streaming-assistant rows keyed by temp ids so
      // we can replace them when the server acks with canonical ids.
      const tempUserKey = `tmp-u-${Date.now()}`;
      const tempAssistantKey = `tmp-a-${Date.now()}`;

      setMessages((prev) => [
        ...prev,
        { key: tempUserKey, role: "user", content, status: "complete" },
        { key: tempAssistantKey, role: "assistant", content: "", status: "streaming" },
      ]);

      const controller = new AbortController();
      streamAbortRef.current = controller;

      let sawAssistantMessage = false;

      try {
        await streamMessage(id, content, {
          signal: controller.signal,
          onEvent: (event, data) => {
            if (event === "user_message") {
              setMessages((prev) =>
                prev.map((m) =>
                  m.key === tempUserKey
                    ? { ...m, id: data.id, created_at: data.created_at, key: `m-${data.id}` }
                    : m
                )
              );
            } else if (event === "delta") {
              const chunk = data.text || "";
              if (!chunk) return;
              setMessages((prev) =>
                prev.map((m) =>
                  m.key === tempAssistantKey
                    ? { ...m, content: (m.content || "") + chunk }
                    : m
                )
              );
            } else if (event === "assistant_message") {
              sawAssistantMessage = true;
              setMessages((prev) =>
                prev.map((m) =>
                  m.key === tempAssistantKey
                    ? {
                        key: `m-${data.id}`,
                        id: data.id,
                        role: "assistant",
                        content: data.content,
                        status: "complete",
                        created_at: data.created_at,
                      }
                    : m
                )
              );
              // The assistant's final message has landed — the tool
              // activity strip has served its purpose; hide it so the
              // finished thread reads cleanly.
              setToolActivity([]);
            } else if (event === "tool_started") {
              setToolActivity((prev) => [
                ...prev,
                {
                  key: `t-${prev.length}-${data.tool_name}`,
                  tool_name: data.tool_name,
                  label: data.label || data.tool_name,
                  status: "running",
                },
              ]);
            } else if (event === "tool_completed") {
              setToolActivity((prev) => {
                // Mark the most recent still-running entry with this
                // tool_name as done. The label/name pair uniquely
                // identifies the started event we're closing out.
                for (let i = prev.length - 1; i >= 0; i -= 1) {
                  if (
                    prev[i].status === "running" &&
                    prev[i].tool_name === data.tool_name
                  ) {
                    const next = prev.slice();
                    next[i] = {
                      ...next[i],
                      status: data.ok ? "completed" : "failed",
                    };
                    return next;
                  }
                }
                return prev;
              });
            } else if (event === "outreach_draft_ready") {
              // Backend just persisted a draft. Render an inline
              // "Review draft" card. If the same draft_id fires again
              // (shouldn't, but be defensive), replace rather than dup.
              const draftId = data.draft_id;
              if (draftId) {
                setOutreachCards((prev) => {
                  const withoutDup = prev.filter((c) => c.draft_id !== draftId);
                  return [...withoutDup, data];
                });
              }
            } else if (event === "valuation_panel") {
              // Backend körde compute_valuation-verktyget. Rendera
              // ValuationMethodPanel inline. Vi taggar panelen med
              // tempAssistantKey så att vid re-run av samma turn
              // (retry) ersätter vi tidigare panel istället för
              // duplicerar. `payload` = compute_all_valuations-shape.
              if (data?.payload) {
                setValuationPanels((prev) => {
                  const withoutDup = prev.filter(
                    (p) => p.anchor_key !== tempAssistantKey,
                  );
                  return [
                    ...withoutDup,
                    {
                      anchor_key: tempAssistantKey,
                      panel_id: `vp-${Date.now()}`,
                      data: data.payload,
                    },
                  ];
                });
              }
            } else if (event === "error") {
              setError(data.detail || "AI-tjänsten svarade inte.");
              // Remove the streaming assistant placeholder — we don't
              // want to leave the empty bubble hanging in the UI.
              setMessages((prev) => prev.filter((m) => m.key !== tempAssistantKey));
              // Also drop tool activity — it can't have led to an
              // answer, and leaving the running chips is misleading.
              setToolActivity([]);
            }
            // 'done' event handled implicitly by stream end
          },
        });
      } catch (e) {
        if (e.name === "AbortError") return;
        setError(e.message || "Kunde inte skicka meddelandet");
        setMessages((prev) => prev.filter((m) => m.key !== tempAssistantKey));
        setToolActivity([]);
      } finally {
        setStreaming(false);
        streamAbortRef.current = null;
        // Fire the "conversation touched" hook so the sidebar list can
        // refetch and re-sort — otherwise the newly-active thread stays
        // buried under older entries.
        if (sawAssistantMessage) onConversationChanged?.();
        inputRef.current?.focus();
      }
    },
    [id, streaming, onConversationChanged]
  );

  // ── Handoff from empty-state (location.state.initialUserMessage) ──
  useEffect(() => {
    if (loading) return;
    if (initialSentRef.current) return;
    const pending = location.state?.initialUserMessage;
    if (!pending) return;
    initialSentRef.current = true;
    // Clear the state so a reload / back-nav doesn't re-send
    navigate(location.pathname, { replace: true, state: {} });
    send(pending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.pathname]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const content = draft.trim();
    if (!content) return;
    setDraft("");
    send(content);
  };

  const handleDelete = async () => {
    if (!conversation) return;
    if (!window.confirm("Ta bort denna konversation?")) return;
    try {
      await deleteConversation(id);
      onConversationChanged?.();
      navigate("/", { replace: true });
    } catch (e) {
      setError(e.message || "Kunde inte ta bort");
    }
  };

  if (loading) {
    return <div className="qai-thread-loading">Laddar konversation…</div>;
  }

  if (error && !conversation) {
    return <div className="qai-error">{error}</div>;
  }

  return (
    <section className="qai-thread">
      {!hideHeader && (
        <header className="qai-thread-header">
          <h2 className="qai-thread-title">{conversation?.title || "Konversation"}</h2>
          {!hideDelete && (
            <button
              type="button"
              className="qai-thread-delete"
              onClick={handleDelete}
              title="Ta bort konversation"
            >
              Ta bort
            </button>
          )}
        </header>
      )}

      <div ref={scrollRef} className="qai-messages-scroll">
        {messages.length === 0 ? (
          <div className="qai-conversation-empty">
            Skriv ett meddelande för att börja.
          </div>
        ) : (
          <div className="qai-messages">
            {messages.map((m) => (
              <MessageBubble key={m.key ?? `m-${m.id}`} message={m} />
            ))}
            {toolActivity.length > 0 && (
              <ToolActivityStrip items={toolActivity} />
            )}
            {outreachCards.map((card) => (
              <OutreachDraftCard
                key={`draft-${card.draft_id}`}
                draft={card}
                onReview={(id) => setReviewingDraftId(id)}
              />
            ))}
            {valuationPanels.map((panel) => (
              <div key={panel.panel_id} className="qai-msg-panel-wrap">
                <ValuationMethodPanel
                  data={panel.data}
                  onMethodSelect={(methodKey) => {
                    // Håll valet inne i konversationen genom att posta
                    // ett follow-up-meddelande. Modellen får då veta
                    // vilken metod säljaren själv valde och kan
                    // adaptera nästa steg (t.ex. "OK, vi räknar då
                    // vidare med substansvärde") utan att panelen
                    // behöver hålla persistent state.
                    const label = METHOD_LABELS[methodKey] || methodKey;
                    send(
                      `Jag vill använda ${label} som värderingsmetod.`,
                    );
                  }}
                />
              </div>
            ))}
            {streaming && !messages.some((m) => m.status === "streaming") && (
              <div className="qai-msg qai-msg-assistant qai-msg-typing">…</div>
            )}
          </div>
        )}
      </div>

      {reviewingDraftId !== null && (
        <OutreachReviewOverlay
          draftId={reviewingDraftId}
          onClose={() => setReviewingDraftId(null)}
          onChange={(updated) => {
            // Reflect status change in the inline card without a full refetch.
            setOutreachCards((prev) =>
              prev.map((c) =>
                c.draft_id === updated.id
                  ? { ...c, status: updated.status, subject: updated.subject }
                  : c
              )
            );
          }}
        />
      )}

      {error && <div className="qai-error qai-error-inline">{error}</div>}

      <form className="qai-input-row" onSubmit={handleSubmit}>
        <textarea
          ref={inputRef}
          className="qai-input"
          placeholder="Ställ en fråga till Qada AI…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit(e);
            }
          }}
          rows={1}
          disabled={streaming}
        />
        <button
          type="submit"
          className="qai-send"
          disabled={streaming || !draft.trim()}
        >
          {streaming ? "…" : "Skicka"}
        </button>
      </form>
    </section>
  );
}

function ToolActivityStrip({ items }) {
  return (
    <div className="qai-tool-strip" role="status" aria-live="polite">
      {items.map((it) => (
        <span
          key={it.key}
          className={
            "qai-tool-chip" +
            (it.status === "running" ? " qai-tool-chip-running" : "") +
            (it.status === "failed" ? " qai-tool-chip-failed" : "")
          }
        >
          {it.status === "running" ? "◌" : it.status === "failed" ? "✕" : "✓"}{" "}
          {it.label}
          {it.status === "running" && <span className="qai-tool-dots">…</span>}
        </span>
      ))}
    </div>
  );
}

function MessageBubble({ message }) {
  const cls =
    message.role === "user"
      ? "qai-msg qai-msg-user"
      : "qai-msg qai-msg-assistant";
  const failed = message.status === "failed";
  return (
    <div className={cls + (failed ? " qai-msg-failed" : "")}>
      {failed ? (
        <em>AI-svaret misslyckades. Försök igen.</em>
      ) : (
        message.content
      )}
    </div>
  );
}
